import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "text-h2 q-px-lg q-py-sm bg-tooltip-and-input-back" }
const _hoisted_2 = {
  key: 0,
  class: "q-mr-sm text-weight-regular"
}
const _hoisted_3 = { class: "text-bold" }
const _hoisted_4 = { class: "q-pl-lg" }

import { useDialogPluginComponent } from "quasar"
import SfxImageViewerManager from "src/components/images/SfxImageViewerManager.vue"
import SfxBreadcrumbs from "src/components/navigation/SfxBreadcrumbs.vue"
import SfxTopNavigationManager from "src/components/navigation/SfxTopNavigationManager.vue"
import SfxSpinner from "src/components/special/SfxSpinner.vue"
import { useSupportStore } from "src/stores/supportStore"
import { ContextMenuItem } from "src/util/classes/contextMenuItem"
import { MANAGER_MAIN_MENU } from "src/util/constants/configurations"
import { ContextAction } from "src/util/enums/entityType"
import { NamedManagerPath } from "src/util/enums/namedPaths"
import { NavigationAction } from "src/util/enums/topNavigation"
import { getImageViewerItems, splitAttachments } from "src/util/functions/attachments"
import { helpTourNotImplemented } from "src/util/functions/helpTour"
import { getIssueCommentData, getIssueData } from "src/util/functions/issues"
import { getItemData } from "src/util/functions/item"
import {
  getCorrectiveWorkOnItemCommentData,
  getCorrectiveWorkOnItemData,
  getRegularWorkOnItemCommentData,
  getRegularWorkOnItemData,
} from "src/util/functions/performedWork"
import {
  getWorkOrderCorrectiveItemCommentData,
  getWorkOrderCorrectiveItemData,
  getWorkOrderRegularItemCommentData,
  getWorkOrderRegularItemData,
} from "src/util/functions/workOrder"
import { getWorkspaceHomeBreadcrumb } from "src/util/functions/workspace"
import { Breadcrumbs } from "src/util/types/components/sfxBreadcrumb"
import { ImageViewerItem } from "src/util/types/components/sfxImageGallery"
import { Attachment, AttachmentContext } from "src/util/types/dataModels/attachment"
import { onBeforeMount, PropType, ref, watch } from "vue"
import { useI18n } from "vue-i18n"

//region Props and Emits


export default /*#__PURE__*/_defineComponent({
  __name: 'SfxPhotoGalleryOverlay',
  props: {
  entityId: {
    type: String,
    required: true,
  },

  entityType: {
    type: String as PropType<AttachmentContext>,
    required: true,
  },

  disableEditImage: Boolean,
},
  emits: [...useDialogPluginComponent.emits],
  setup(__props) {

const props = __props



//endregion

//region Variables

const { t } = useI18n()
const supportStore = useSupportStore()
const { dialogRef, onDialogOK } = useDialogPluginComponent()

const menuContent = MANAGER_MAIN_MENU
const title = ref<string>()
const titleReference = ref<string>()
const breadcrumbs = ref<Breadcrumbs>([])

const loadingImages = ref(true)
const spinnerText = t("sfxSpinner.attachment_data")

const entity = ref()
const imageList = ref<Attachment[]>([])
const attachmentList = ref<Attachment[]>([])
const entityImages = ref<ImageViewerItem[]>([])

//endregion

//region Lifecycle

onBeforeMount(async () => {
  entity.value = await getEntityData(props.entityId, props.entityType)
  if (entity.value) await splitAttachments(entity.value, imageList.value, attachmentList.value)

  await setTitleAndBreadcrumbData(props.entityType)
  supportStore.setFeedbackData(t("attachments.photo_gallery"))
})

//endregion

//region Functions

async function handleContextSelect(contextMenuItem: ContextMenuItem) {
  if (contextMenuItem.action === ContextAction.HELP) {
    return helpTourNotImplemented("SfxPhotoGallery")
  }
}

async function getEntityData(entityId: string, entity: AttachmentContext) {
  switch (entity) {
    case "item":
      return await getItemData(entityId)

    case "issue_item":
      return await getIssueData(entityId)

    case "issue_item_comment":
      return await getIssueCommentData(entityId)

    case "corrective_work_item":
      return await getCorrectiveWorkOnItemData(entityId)

    case "regular_work_item":
      return await getRegularWorkOnItemData(entityId)

    case "corrective_work_item_comment":
      return await getCorrectiveWorkOnItemCommentData(entityId)

    case "regular_work_item_comment":
      return await getRegularWorkOnItemCommentData(entityId)

    case "work_order_corrective_item":
      return await getWorkOrderCorrectiveItemData(entityId)

    case "work_order_regular_item":
      return await getWorkOrderRegularItemData(entityId)

    case "work_order_corrective_item_comment":
      return await getWorkOrderCorrectiveItemCommentData(entityId)

    case "work_order_regular_item_comment":
      return await getWorkOrderRegularItemCommentData(entityId)
  }
}

async function setTitleAndBreadcrumbData(entityType: AttachmentContext) {
  let entityBreadcrumb: Breadcrumbs = []
  const photoGallery: Breadcrumbs = [{ text: t("attachments.photo_gallery") }]

  switch (entityType) {
    case "item": {
      title.value = entity.value.designation || ""

      entityBreadcrumb = getItemBreadcrumbs()
      break
    }

    case "issue_item": {
      titleReference.value = entity.value.reference
      title.value = entity.value.title || ""

      entityBreadcrumb = getIssueItemBreadcrumbs()
      break
    }

    case "issue_item_comment": {
      const issue = await getIssueData(entity.value.issue)
      titleReference.value = issue?.reference || ""
      title.value = issue?.title || ""

      entityBreadcrumb = getIssueItemBreadcrumbs()
      break
    }

    case "corrective_work_item": {
      title.value = entity.value.title || ""

      entityBreadcrumb = getCorrectiveWorkItemBreadcrumbs()
      break
    }

    case "regular_work_item": {
      title.value = entity.value.title || ""

      entityBreadcrumb = getRegularWorkItemBreadcrumbs()
      break
    }

    case "corrective_work_item_comment": {
      const work = await getCorrectiveWorkOnItemData(entity.value.performed_work)
      title.value = work?.title || ""

      entityBreadcrumb = getCorrectiveWorkItemBreadcrumbs()
      break
    }

    case "regular_work_item_comment": {
      const work = await getRegularWorkOnItemData(entity.value.performed_work)
      title.value = work?.title || ""

      entityBreadcrumb = getRegularWorkItemBreadcrumbs()
      break
    }

    case "work_order_corrective_item":
    case "work_order_corrective_item_comment": {
      const workOrder = await getWorkOrderCorrectiveItemData(entity.value.work_order)
      titleReference.value = workOrder?.reference || ""
      title.value = workOrder?.designation || ""

      entityBreadcrumb = getWorkOrderBreadcrumbs()
      break
    }

    case "work_order_regular_item":
    case "work_order_regular_item_comment": {
      const workOrder = await getWorkOrderRegularItemData(entity.value.work_order)
      titleReference.value = workOrder?.reference || ""
      title.value = workOrder?.designation || ""

      entityBreadcrumb = getWorkOrderBreadcrumbs()
      break
    }

    case "work_order_corrective_area_comment":
    case "work_order_regular_area_comment": {
      // TODO: Do me at some point
      break
    }
  }

  breadcrumbs.value = [await getWorkspaceHomeBreadcrumb(), ...entityBreadcrumb, ...photoGallery]
}

function getItemBreadcrumbs() {
  return [
    {
      text: t("items.title"),
      link: { name: NamedManagerPath.SELECT_ITEM },
      highlight: true,
    },
    {
      text: t("items.item_overview"),
    },
  ]
}

function getIssueItemBreadcrumbs() {
  return [
    {
      text: t("issues.title"),
      link: { name: NamedManagerPath.SELECT_ISSUE },
      highlight: true,
    },
    {
      text: t("issues.issue_overview"),
    },
  ]
}

function getCorrectiveWorkItemBreadcrumbs() {
  return [
    {
      text: t("performedWork.title"),
      link: { name: NamedManagerPath.SELECT_PERFORMED_WORK },
      highlight: true,
    },
    {
      text: t("performedWork.title_corrective_work"),
    },
  ]
}

function getRegularWorkItemBreadcrumbs() {
  return [
    {
      text: t("performedWork.title"),
      link: { name: NamedManagerPath.SELECT_PERFORMED_WORK },
      highlight: true,
    },
    {
      text: t("performedWork.title_regular_work"),
    },
  ]
}

function getWorkOrderBreadcrumbs() {
  return [
    {
      text: t("workOrder.title"),
      link: { name: NamedManagerPath.SELECT_WORK_ORDER },
      highlight: true,
    },
    {
      text: t("workOrder.title_overview"),
    },
  ]
}

//endregion

//region Setup Code

watch(
  () => imageList.value,
  async (images) => {
    loadingImages.value = true

    const items = await getImageViewerItems(props.entityType, props.entityId, images)
    if (items) entityImages.value = items

    loadingImages.value = false
  },
  { immediate: true, deep: true }
)

//endregion

return (_ctx: any,_cache: any) => {
  const _component_q_header = _resolveComponent("q-header")!
  const _component_q_card_section = _resolveComponent("q-card-section")!
  const _component_q_layout = _resolveComponent("q-layout")!
  const _component_q_card = _resolveComponent("q-card")!
  const _component_q_dialog = _resolveComponent("q-dialog")!

  return (_openBlock(), _createBlock(_component_q_dialog, {
    ref_key: "dialogRef",
    ref: dialogRef,
    "transition-show": "slide-up",
    "transition-hide": "slide-down",
    maximized: ""
  }, {
    default: _withCtx(() => [
      _createVNode(_component_q_card, { class: "q-dialog-plugin" }, {
        default: _withCtx(() => [
          _createVNode(_component_q_layout, { view: "hHh lpR fff" }, {
            default: _withCtx(() => [
              _createVNode(_component_q_header, { class: "bg-transparent text-navy-base" }, {
                default: _withCtx(() => [
                  _createVNode(SfxTopNavigationManager, {
                    "main-menu-content": _unref(menuContent),
                    "disable-extended-menu": "",
                    "disable-main-menu": "",
                    "hide-search-bar": "",
                    "hide-quick-actions": "",
                    "hide-separator": "",
                    "hide-notifications": "",
                    "hide-todo-icon": "",
                    "hide-user-display": "",
                    onContextSelect: handleContextSelect
                  }, null, 8, ["main-menu-content"]),
                  _createElementVNode("div", _hoisted_1, [
                    (titleReference.value)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_2, _toDisplayString(titleReference.value), 1))
                      : _createCommentVNode("", true),
                    _createElementVNode("span", _hoisted_3, _toDisplayString(title.value), 1)
                  ]),
                  _createElementVNode("div", _hoisted_4, [
                    _createVNode(SfxBreadcrumbs, {
                      breadcrumbs: breadcrumbs.value,
                      "navigation-action": _unref(NavigationAction).CLOSE,
                      "is-dialog": "",
                      onClose: _cache[0] || (_cache[0] = ($event: any) => (_unref(onDialogOK)(entityImages.value))),
                      class: "bg-tooltip-and-input-back"
                    }, null, 8, ["breadcrumbs", "navigation-action"])
                  ])
                ]),
                _: 1
              }),
              _createVNode(_component_q_card_section, { class: "bg-tooltip-and-input-back window-height" }, {
                default: _withCtx(() => [
                  (loadingImages.value)
                    ? (_openBlock(), _createBlock(SfxSpinner, {
                        key: 0,
                        class: "absolute",
                        text: _unref(spinnerText)
                      }, null, 8, ["text"]))
                    : (_openBlock(), _createBlock(SfxImageViewerManager, {
                        key: 1,
                        "entity-id": __props.entityId,
                        "entity-type": __props.entityType,
                        "image-list": entityImages.value,
                        "onUpdate:imageList": _cache[1] || (_cache[1] = ($event: any) => ((entityImages).value = $event)),
                        "start-image": entityImages.value[0],
                        "disable-edit-image": __props.disableEditImage
                      }, null, 8, ["entity-id", "entity-type", "image-list", "start-image", "disable-edit-image"]))
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 512))
}
}

})